import { CheckCircle2 } from "lucide-react";
import { IMAGES, CANDIDATE_JOURNEY, RECRUITMENT_AREAS } from "../data/site";
import PageHero from "../components/common/PageHero";
import SectionHeading from "../components/common/SectionHeading";
import Reveal from "../components/common/Reveal";
import JourneyStepper from "../components/common/JourneyStepper";
import CandidateIntake from "../components/intake/CandidateIntake";
import useSeo from "../hooks/useSeo";

export default function Candidates() {
  useSeo({
    title: "For Candidates — Submit Your CV",
    description:
      "Nurses, healthcare assistants, allied health professionals and medical support staff: submit your CV securely and get supportive, transparent recruitment guidance from screening to opportunity matching.",
    keywords:
      "Nurse Jobs, Healthcare Assistant Jobs, Allied Health Professional Recruitment, Submit CV, Healthcare Careers",
  });
  return (
    <>
      <PageHero
        overline="Jobseekers"
        title="Your next role, supported every step"
        description="Healthcare professionals — and talented people across every sector — trust us for a transparent, supportive and intelligent recruitment experience."
      />

      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal className="lg:order-2">
            <div className="overflow-hidden rounded-2xl border border-border shadow-sm">
              <img src={IMAGES.candidateDetail} alt="Healthcare professional in a supportive interview" className="h-[440px] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100} className="lg:order-1">
            <span className="overline text-xs text-gold">Why Candidates Choose Us</span>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-navy sm:text-4xl">A recruitment experience built around you</h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              We listen to your goals and champion your career at every stage — guiding you through
              screening, compliance, interviews and onboarding with honest, expert support and a
              genuine commitment to finding the right role for you.
            </p>
            <ul className="mt-6 space-y-3">
              {["Secure, confidential CV submission", "Support with compliance and right-to-work", "Interview preparation and coordination", "Guidance through onboarding and beyond"].map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-charcoal">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading overline="Candidate Journey" title="What to expect" description="A clear, supportive process from CV to placement support." />
          <div className="mt-16"><JourneyStepper steps={CANDIDATE_JOURNEY} accent="navy" /></div>
        </div>
      </section>

      <section id="submit" className="scroll-mt-24 py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center">
            <span className="overline text-xs text-gold">Submit Your CV</span>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-navy sm:text-4xl">Share your details securely</h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Upload your CV and tell us about your experience. Our team will be in touch about suitable opportunities.</p>
          </div>
          <div className="mt-10 rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
            <CandidateIntake />
          </div>
        </div>
      </section>
    </>
  );
}
